import { Path } from 'react-hook-form';

import { TOption, TTableColumn, TTableRow } from '@treezor/design-system';

import { IPagination } from '@/store/types/search.types';

import { TAppError } from '@/store/types/base.state';

export const periodBases = {
  ON: 'ON',
  BEFORE: 'BEFORE',
  AFTER: 'AFTER',
  BETWEEN: 'BETWEEN',
} as const;

export type TPeriodBasesType = keyof typeof periodBases;

export const rangeBases = {
  EQUAL: 'EQUAL',
  LESS: 'LESS',
  MORE: 'MORE',
  BETWEEN: 'BETWEEN',
} as const;

export type TRangeBasesType = keyof typeof rangeBases;

export interface IStandardFilter<T> {
  label: string;
  name: T;
}

export interface IFilterFields<T> {
  title: string;
  label?: string;
  name: Path<T>;
  onChange?: () => void;
}

export interface IPeriod {
  from: string;
  to: string;
  base: TPeriodBasesType;
}

export interface IRange {
  from?: string;
  to?: string;
  base?: TRangeBasesType;
}

export interface IInputField {
  value?: string;
  handleChange: (value: string) => void;
}

export interface IPeriodField {
  value?: IPeriod;
  handleChange: (value: IPeriod) => void;
}

export interface IRangeField {
  value?: IRange;
  handleChange: (value: IRange) => void;
}

export function isPeriodField(field: TField): field is IPeriodField | IRangeField {
  return (
    (field as IPeriodField | IRangeField).value?.from !== undefined ||
    (field as IPeriodField | IRangeField).value?.to !== undefined ||
    (field as IPeriodField | IRangeField).value?.base !== undefined
  );
}
export interface ISelectField {
  value?: TOption;
  options: TOption[];
  handleChange: (value: string | number | boolean) => void;
}

export function isSelectField(field: TField): field is ISelectField {
  return (field as ISelectField).value?.label !== undefined && (field as ISelectField).value?.value !== undefined;
}

export interface ILookupField {
  value?: string | number | boolean;
  options?: TOption[];
  handleChange: (value: string | number | boolean) => void;
}

export interface ISearch {
  key: string;
  value: string;
}
export interface ISearchField {
  value?: ISearch;
  handleChange: (value: ISearch) => void;
  options?: TOption[];
}

export function isSearchField(field: TField): field is ISearchField {
  return (field as ISearchField).value?.key !== undefined && (field as ISearchField).value?.value !== undefined;
}

export interface ICheckOption extends TOption {
  children?: ICheckOption[];
  isDisabled?: boolean;
}
export interface ICheckField {
  values?: ICheckOption[];
  options: ICheckOption[];
  handleChange: (value: (string | number | boolean)[]) => void;
}

export function isCheckField(field: TField): field is ICheckField {
  return (field as ICheckField).values !== undefined;
}

export interface IButtonField<T extends string | number | string[] | number[] = string> {
  value?: T;
  handleChange: (value: T) => void;
  values?: TOption[];
}

export function isButtonField(field: TField): field is IButtonField {
  return (field as IButtonField).values !== undefined;
}

export type TField =
  | ISelectField
  | ILookupField
  | IInputField
  | IPeriodField
  | IRangeField
  | ISearchField
  | ICheckField
  | IButtonField<string | number | string[] | number[]>;

export type TUseFiltersdReturn<T> = T & {
  clearFilters: () => void;
  hasFilters: boolean;
};

export type TUseTableDataReturn<Y, T extends Record<string, TField>> = {
  data: unknown[];
  fields: T;
  rows: TTableRow<Y>[];
  headers: TTableColumn<Y>[];
  isLoading: boolean;
  hasFilters: boolean;
  totalRows?: number;
  currentPage?: number;
  onPageChange?: (pageNumber: number) => void;
  pagination?: IPagination;
  hasData?: boolean;
  error?: TAppError;
  clearFilters?: () => void;
  onSort?: (field: keyof Y, isSortedAtoZ?: boolean) => void;
  onPerPage?: (perPage: number) => void;
  perPage?: number;
};

export function parseAllParameters<F, T>(filters: F, parseFunction: (key: keyof F, value: F[keyof F]) => T): T {
  return Object.keys(filters).reduce<T>((prev, current: string) => {
    return {
      ...prev,
      ...parseFunction(current as keyof F, filters[current]),
    };
  }, {} as T);
}
